import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { CustomerBranding } from '@/components/dashboards/admin/CustomerBranding';
import { usePhishingFollowUpEnabled } from '@/hooks/usePhishingFollowUpEnabled';
import { FlexContainer } from '@/ui/styled-ui';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { LearnerProvider } from './context';

const StyledPageContainer = styled(FlexContainer)`
  position: relative;
  min-height: 100%;
  @media screen and (max-width: 860px) {
    padding-top: 0;
  }
`;

const StyledTitle = styled.h1`
  margin: 0;
  font-weight: 700;
  font-size: var(--body-font-size);
  line-height: var(--body-line-height);
  text-transform: capitalize;
`;

const StyledSectionTabRow = styled.div`
  display: flex;
  width: 100%;
  height: 82px;
  border: 1px solid var(--gray-moderate);
  border-radius: 20px;
  overflow: visible;
  margin-top: var(--default-padding);

  @media screen and (max-width: 480px) {
    height: 56px;
  }
`;

const StyledSectionTab = styled.button<{ $active: boolean }>`
  all: unset;
  cursor: pointer;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  font-weight: 600;
  font-size: var(--body-font-size);
  line-height: 33px;
  position: relative;

  @media screen and (max-width: 480px) {
    padding: var(--medium-padding);
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
  }
  color: var(--black-main);
  background-color: ${({ $active }) =>
    $active ? 'var(--beige-main)' : 'transparent'};
  transition: background-color 0.2s ease;
  border-right: 1px solid var(--gray-moderate);

  &:first-child {
    border-radius: 20px 0 0 20px;
  }

  &:last-child {
    border-right: none;
    border-radius: 0 20px 20px 0;
  }

  &:hover {
    background-color: ${({ $active }) =>
      $active ? 'var(--beige-main)' : 'var(--gray-pale)'};
  }

  &:focus-visible {
    outline: 2px solid var(--red-dark);
    outline-offset: -2px;
  }

  ${({ $active }) =>
    $active &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 27px;
      height: 15px;
      background-color: var(--beige-main);
      clip-path: polygon(0 0, 100% 0, 50% 100%);
      z-index: 1;
    }
  `}
`;

type LearnerLayoutProps = {
  children: ReactNode;
  isLoading: boolean;
};

export const LearnerLayout: React.FC<LearnerLayoutProps> = ({
  children,
  isLoading,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enabled: showPhishingTab } = usePhishingFollowUpEnabled();

  const phishingPath = RedirectPaths[RedirectPathsEnum.LEARNER_PHISHING]();
  const awarenessPath =
    RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE]();

  const isPhishingActive = location.pathname.startsWith(phishingPath);

  return (
    <>
      <CustomerBranding isLearner />
      <StyledTitle>{t('content.trainings')}</StyledTitle>
      {showPhishingTab && (
        <StyledSectionTabRow>
          <StyledSectionTab
            $active={!isPhishingActive}
            onClick={() => navigate(awarenessPath)}
          >
            {t('generic.awareness')}
          </StyledSectionTab>
          <StyledSectionTab
            $active={isPhishingActive}
            onClick={() => navigate(phishingPath)}
          >
            {t('generic.phishing')}
          </StyledSectionTab>
        </StyledSectionTabRow>
      )}
      <LearnerProvider isLoading={isLoading}>
        <StyledPageContainer
          direction="column"
          justify="flex-start"
          align="flex-start"
          className="w-full"
        >
          {children}
        </StyledPageContainer>
      </LearnerProvider>
    </>
  );
};
